import { DataManager } from '@syncfusion/ej2-data';
import { Component } from '@angular/core';
import { DragAndDropEventArgs, FieldsSettingsModel, ToolbarModule, TreeViewModule } from '@syncfusion/ej2-angular-navigations';
import { AdministrationItemType, DetailsInitInfo, IItemData, ItemEventInfo, ITreeNodeData } from '../../data-structures';
import { ClientService } from 'src/app/base/services/client/client.service';
import { AvpCustomAdaptor } from 'src/app/base/shared/syncfusion/avp-custom-adaptor';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ToastService } from 'src/app/base/services/toast.service';
import { ITreeViewComponent } from '../tree-view.interface';
import { TreeViewConfigBasedService } from './tree-view-config-based.service';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { TreeViewBaseComponent } from '../tree-view-base.component';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { IConfigBasedStructuralEntryDTO, ParkingSubscriptionDTO } from 'src/app/base/models/models.swagger.generated';
import { AuthService } from 'src/app/base/services/auth.service';

@Component({
    selector: 'app-tree-view-config-based',
    templateUrl: './tree-view-config-based.component.html',
    styleUrls: ['./tree-view-config-based.component.scss'],
    imports: [TooltipModule, ToolbarModule, TreeViewModule, NgClass, TranslateModule]
})
export class TreeViewConfigBasedComponent extends TreeViewBaseComponent implements ITreeViewComponent {
  public treeViewFieldSetting: FieldsSettingsModel;

  public canAddTariff = false;
  public canRemoveTariff = false;
  public canAddSubscription = false;
  public canAddStructuralEntry = false;
  public canRemoveStructuralEntry = false;
  public canRemoveSubscription = false;

  public tooltipAnimation = { open: { effect: 'FadeIn', delay: 500 }, close: { effect: 'None' } };

  private fieldNames: FieldsSettingsModel = {
    id: 'id',
    parentID: 'parentId',
  };

  private get itemCache() {
    return this.service.itemCache;
  }

  constructor(private service: TreeViewConfigBasedService,
              clientService: ClientService,
              private spinnerService: SpinnerService,
              modalService: ModalService,
              private toastService: ToastService,
              private authService: AuthService
            ) {
    super(clientService, modalService);
    const dataSource = new DataManager({
      adaptor: new AvpCustomAdaptor(this.service, this.spinnerService, false, true)
    });
    this.treeViewFieldSetting = Object.assign({dataSource}, this.fieldNames);
  }

  public getTreeItemClasses(item: IItemData): string[] {
    const classes = [];
    classes.push(this.getIconClassByType(item.type));
    if (item.dto?.isSubscriptionSet === true) {
      classes.push('subscription-set');
    }
    return classes;
  }

  // methods from ITreeViewComponent
  public addNode(eventInfo: ItemEventInfo): void {
    const parentId = this.parentInfo.item.id;
    const parentNodeId = this.parentInfo.item.treeNodeId;
    const createdNode = this.handleAddNodeEvent(eventInfo, this.service.getNextNodeId(eventInfo.id, eventInfo.type));
    if (createdNode) {
      // if node was not added to parent since it was not collapsed yet
      this.itemCache.addOrUpdateItem(createdNode.itemData);
    }
    if (eventInfo.type === AdministrationItemType.Tariff) {
      this.addTariffNodeOnSubscriptionNodes(parentId, createdNode, parentNodeId);
    }
    else if (eventInfo.type === AdministrationItemType.Subscription) {
      const item = this.itemCache.getCachedItem(parentId, AdministrationItemType.ConfigBasedStructuralElement);
      if (item) {
        (item.itemData.dto as IConfigBasedStructuralEntryDTO).hasSubscriptions = true;
      }
    }
    else if (eventInfo.type === AdministrationItemType.ConfigBasedStructuralElement) {
      const item = this.itemCache.getCachedItem(parentId, AdministrationItemType.ConfigBasedStructuralElement);
      if (item) {
        (item.itemData.dto as IConfigBasedStructuralEntryDTO).hasChildEntries = true;
      }
    }
    this.updateCanAddXyFlags();
  }

  public removeNode(eventInfo: ItemEventInfo): void {
    this.handleRemoveNodeEvent(eventInfo);
    if (eventInfo.type === AdministrationItemType.Tariff) {
      this.removeTariffNodes(eventInfo.data.id);
      this.itemCache.removeItem(eventInfo.id, eventInfo.type);
    }
  }

  protected isItemInactive(eventInfo: ItemEventInfo): boolean {
    if (eventInfo.type === AdministrationItemType.Tariff || eventInfo.type === AdministrationItemType.Subscription) {
      return !(eventInfo.data as any).active;
    }
    if (eventInfo.type === AdministrationItemType.ZoneDevice) {
      return !(eventInfo.data as any).enabled;
    }
    return false;
  }

  public updateNode(eventInfo: ItemEventInfo): void {
    this.handleUpdateNodeEvent(eventInfo);
  }
  // end methods from ITreeViewComponent

  public onNodeDragStart(args: DragAndDropEventArgs) {
    const info = this.parseNodeId(args.draggedNodeData.id as string) as DetailsInitInfo;
    if (info.type === AdministrationItemType.ConfigBasedStructuralElementCollection) {
      args.cancel = true;
    }

    if (info.type === AdministrationItemType.Tariff) {
      const nodeData = this.tree.getTreeData(args.draggedNodeData.id as string)?.at(0)?.itemData as IItemData;
      const parentNodeData = this.tree.getTreeData(args.draggedNodeData.parentID as string)?.at(0)?.itemData as IItemData;
      if (parentNodeData?.dto?.active && nodeData?.dto?.active) {
        args.cancel = true;
        this.toastService.showWarning('i18n.domain.administration.overview.messages.active-item-cannot-be-moved-title', 'i18n.domain.administration.overview.messages.active-item-cannot-be-moved-message');
      }
    }
  }

  public override onNodeDragStop(args: DragAndDropEventArgs): void {
    super.onNodeDragStop(args);
    if (args.cancel) {
      return;
    }

    // all actions need to be handled within this method programmatically, because of proper positioning within parent node
    args.cancel = true;

    const info = this.parseNodeId(args.draggedNodeData.id as string) as DetailsInitInfo;
    const sourceInfo = this.parseNodeId(args.draggedNodeData.parentID as string) as DetailsInitInfo;
    const targetInfo = this.parseNodeId(args.droppedNodeData.id as string) as DetailsInitInfo;

    switch (info.type) {
      case AdministrationItemType.ConfigBasedStructuralElement:
        this.service.moveStructuralEntry(info.id, sourceInfo.id, targetInfo.id).subscribe(() => {
          this.moveNode(args.draggedNodeData.id as string, args.droppedNodeData.id as string);
        });
        return;
      case AdministrationItemType.Tariff:
        this.service.moveTariff(info.id, targetInfo.type === AdministrationItemType.Subscription ? targetInfo.id : null).subscribe(() => {
          if (sourceInfo.type === AdministrationItemType.Subscription) {
            // remove tariff nodes from subscription nodes with same subscriptionId
            const sourceNodes = this.findAllNodesOfTree(sourceInfo.id, AdministrationItemType.Subscription);
            const sourceNodeIds = sourceNodes.map((node) => node.id);
            this.findAllNodesOfTree(info.id, AdministrationItemType.Tariff).forEach((node) => {
              if (node.id === args.draggedNodeData.id) {
                return;
              }
              if (sourceNodeIds.find((value) => value === node.parentId)) {
                this.tree.removeNodes([node.id]);
              }
            });

            // add tariff nodes to subscription nodes with same subscriptionId
            const nodeData = this.tree.getTreeData(args.draggedNodeData.id as string)?.at(0) as any as ITreeNodeData;
            const targetNodes = this.findAllNodesOfTree(targetInfo.id, AdministrationItemType.Subscription);
            targetNodes.forEach((targetNode) => {
              if (targetNode.id === args.droppedNodeData.id) {
                return;
              }
              if (!targetNode.expanded && (!targetNode.child || targetNode.child.length === 0)) {
                // node was not expanded yet
                targetNode.hasChildren = true;
                this.tree.refreshNode(targetNode.id as string, targetNode as any);
                return;
              }
              const newNodeData: ITreeNodeData = {
                id: this.service.getNextNodeId(info.id, AdministrationItemType.Tariff),
                hasChildren: false,
                itemData: nodeData.itemData,
              }
              this.addNodeAtProperPosition(newNodeData, targetNode as any);
              this.tree.refreshNode(targetNode.id as string, targetNode as any);
            });
          }

          this.moveNode(args.draggedNodeData.id as string, args.droppedNodeData.id as string);
        });

        return;
      case AdministrationItemType.Subscription:
        const isCopyMode = this.isCopyMode(args);
        if (isCopyMode){
          this.service.addSubscriptionToElement(info.id, targetInfo.id).subscribe((treeNode) => {
            this.addNodeAtProperPositionByParentNodeId(treeNode, args.droppedNodeData.id as string);
            this.updateNode(treeNode);
          });
        } else {
          this.service.moveSubscriptionFromElement(info.id, sourceInfo.id, targetInfo.id).subscribe((dto: ParkingSubscriptionDTO) => {
            if (targetInfo.type == AdministrationItemType.SubscriptionCollection && dto.structuralEntries?.length >= 1) {
              // don't add subscription to unassigned subscriptions node if we still have assignments
              return;
            }
            this.moveNode(args.draggedNodeData.id as string, args.droppedNodeData.id as string);
          });
        }

        return;
    }
  }

  public onRemoveStructuralEntry() {
    const dialog = this.modalService.showOkCancelDialog('i18n.domain.administration.overview.messages.structural-entry-delete-title', 'i18n.domain.administration.overview.messages.structural-entry-delete-question');
    dialog.onOkSubject.subscribe(() => {
      this.service.deleteStructuralEntry(this.selectedItem.id, this.selectedItem.parentId).subscribe(() => {
        this.tree.removeNodes([this.selectedItem.treeNodeId]);
        this.setSelectedItem(null);
        this.tree.selectedNodes = [];
        this.updateCanAddXyFlags();
      });
    });
  }

  public onRemoveSubscription() {
    const dialog = this.modalService.showOkCancelDialog('i18n.domain.administration.overview.messages.subscription-detach-title', 'i18n.domain.administration.overview.messages.subscription-detach-question');
    dialog.onOkSubject.subscribe(() => {
      this.service.deleteSubscriptionFromElement(this.selectedItem.id, this.selectedItem.parentId).subscribe((dto: ParkingSubscriptionDTO) => {
        if (dto.structuralEntries == null || dto.structuralEntries.length === 0) {
          // move subscription to unassigned subscriptions node
          this.moveNode(this.selectedItem.treeNodeId, this.service.subscriptionCollectionId);
        } else {
          this.tree.removeNodes([this.selectedItem.treeNodeId]);
        }
        this.setSelectedItem(null);
        this.tree.selectedNodes = [];
        this.updateCanAddXyFlags();
      });
    });
  }

  public onAddStructuralEntry() {
    this.onAddNewItem(AdministrationItemType.ConfigBasedStructuralElement);
  }

  public onAddTariff() {
    this.onAddNewItem(AdministrationItemType.Tariff);
  }

  public onAddSubscription() {
    this.onAddNewItem(AdministrationItemType.Subscription);
  }

  protected updateCanAddXyFlags() {
    // because of change after check issue, we need to use flags and cannot use getter methods
    this.canAddStructuralEntry = !!(this.selectedItem && (this.selectedItem?.type === AdministrationItemType.ConfigBasedStructuralElementCollection || (this.selectedItem?.id && this.selectedItem?.type === AdministrationItemType.ConfigBasedStructuralElement)));
    this.canRemoveStructuralEntry = !!(this.selectedItem?.id && this.selectedItem?.type === AdministrationItemType.ConfigBasedStructuralElement && !(this.selectedItem?.itemData?.dto as IConfigBasedStructuralEntryDTO).hasChildEntries && !(this.selectedItem?.itemData?.dto as IConfigBasedStructuralEntryDTO).hasSubscriptions);
    this.canAddTariff = !!(this.selectedItem?.id && this.selectedItem?.type === AdministrationItemType.Subscription);
    this.canRemoveTariff = !!(this.selectedItem?.id && this.selectedItem?.type === AdministrationItemType.Tariff);
    this.canAddSubscription = !!(this.selectedItem?.id && this.selectedItem?.type === AdministrationItemType.ConfigBasedStructuralElement);
    this.canRemoveSubscription = !!(this.selectedItem?.id && this.selectedItem?.type === AdministrationItemType.Subscription);
  }

  protected canDropNode(args: DragAndDropEventArgs): boolean {
    const draggedNode = args.draggedNodeData;
    const droppedNode = args.droppedNodeData;
    const isCopyMode = this.isCopyMode(args);
    if (draggedNode == null || droppedNode == null || draggedNode.parentID === droppedNode.id) {
      return false;
    }

    const info = this.parseNodeId(draggedNode.id as string) as DetailsInitInfo;
    const targetInfo = this.parseNodeId(droppedNode.id as string) as DetailsInitInfo;
    switch (info.type) {
      case AdministrationItemType.ConfigBasedStructuralElement:
        return (targetInfo.type === AdministrationItemType.ConfigBasedStructuralElementCollection || targetInfo.type === AdministrationItemType.ConfigBasedStructuralElement) && !isCopyMode;
      case AdministrationItemType.Tariff:
        const targetNodeData = this.tree.getTreeData(droppedNode.id as string)?.at(0)?.itemData as IItemData;
        return ((targetInfo.type === AdministrationItemType.Subscription && (!targetNodeData.dto?.active || this.authService.isUserAdmin())) || targetInfo.type === AdministrationItemType.TariffCollection) && !isCopyMode;
      case AdministrationItemType.Subscription:
        return targetInfo.type === AdministrationItemType.ConfigBasedStructuralElement || targetInfo.type === AdministrationItemType.SubscriptionCollection;
      default:
        return false;
    }
  }

  protected parseNodeId(nodeId: string): DetailsInitInfo {
    return this.service.parseNodeId(nodeId);
  }

  private addTariffNodeOnSubscriptionNodes(subscriptionId: number, tariffNode: ITreeNodeData, ignoreNodeId: string = null) {
    const affectedSubscriptions = this.findAllNodesOfTree(subscriptionId, AdministrationItemType.Subscription);
    affectedSubscriptions.forEach((subscription) => {
      if (subscription.id === ignoreNodeId) {
        return;
      }

      const dto = subscription.itemData?.dto as ParkingSubscriptionDTO;
      if (dto) {
        subscription.hasChildren = dto.tariffs?.length > 0;
      }

      const nodeData: ITreeNodeData = {
        id: this.service.getNextNodeId(tariffNode.itemData.dto.id, AdministrationItemType.Tariff),
        hasChildren: false,
        itemData: tariffNode.itemData,
      }
      this.addNodeAtProperPosition(nodeData, subscription as any);
      this.tree.refreshNode(subscription.id as string, subscription as any);
    });
  }

  private removeTariffNodes(tariffId: number) {
    const affectedTariffNodes = this.findAllNodesOfTree(tariffId, AdministrationItemType.Tariff);
    affectedTariffNodes.forEach((tariffNode) => {
      this.tree.removeNodes([tariffNode.id]);
    });
  }
}
